import React, { useState } from 'react';
import { Modal, Table, Typography, Input } from '@crisellevaldez/speculo-ui';
import { EditContactModal } from '../contacts/EditContactModal';
import { ExpandableTagsCell } from '../../common/ExpandableTagsCell';
import { ChannelConsentIcons } from '../contacts/consent/ChannelConsentIcons';
import { formatNumber } from '../../../utils/format';
import { formatPhoneNumber } from '../../../utils/formatters';
import { formatDate } from '../../../utils/dateTime';
import { useFlowContactsGQL, PAGE_SIZE } from '../../../hooks/graphql/flows/useFlowContactsGQL';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';
import { useDebouncedSearch } from '../../../hooks/useDebouncedSearch';
import { Search } from 'lucide-react';
import { OffsetPagination } from '../../common/OffsetPagination';
import { TableCard } from '../../common/TableCard';
import { Contact } from '../../../types/contacts';

interface ViewFlowContactsModalProps {
  isOpen: boolean;
  onClose: () => void;
  flowId: string;
}

export const ViewFlowContactsModal: React.FC<ViewFlowContactsModalProps> = ({
  isOpen,
  onClose,
  flowId,
}) => {
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebouncedSearch(searchInput);
  const [sortField, setSortField] = useState('flowCreatedAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const { organization } = useOrganizationStore();
  const { contacts, total, isLoading, currentPage, setCurrentPage, refetch } = useFlowContactsGQL(
    flowId,
    organization?.id || '',
    { search: debouncedSearch, sortBy: sortField, sortOrder: sortDirection }
  );

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <Modal.Header>
          <div>
            <Typography variant="h3">
              Flow Contacts
            </Typography>
            <div className="text-sm text-muted-foreground">
              {formatNumber(total)} {total === 1 ? 'record' : 'records'}
            </div>
          </div>
        </Modal.Header>
        <div className="p-6">
          <div className="mb-4">
            <Input
              placeholder="Search by name, email or phone..."
              startIcon={<Search className="text-muted-foreground" size={16} />}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="h-8 text-sm"
            />
          </div>
          <TableCard
            className="h-[calc(100dvh-200px)] sm:h-[calc(100dvh-220px)] md:h-[calc(100dvh-250px)] 3xl:h-[calc(100vh-500px)]"
            footer={
              <OffsetPagination
                currentPage={currentPage}
                totalItems={total}
                pageSize={PAGE_SIZE}
                onNextPage={() => setCurrentPage((p) => p + 1)}
                onPrevPage={() => setCurrentPage((p) => Math.max(0, p - 1))}
                onPageChange={setCurrentPage}
                isLoading={isLoading}
                label="contacts"
              />
            }
          >
            <Table<Contact>
              columns={[
                {
                  key: 'fullName',
                  header: 'Full Name',
                  width: '250px',
                  resizable: true,
                  cell: (row) => (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedContactId(row.id);
                      }}
                      className="font-medium text-left text-primaryfortext underline cursor-pointer"
                    >
                      {row.fullName || '-'}
                    </button>
                  ),
                },
                {
                  key: 'phoneNumber',
                  header: 'Phone Number',
                  width: '150px',
                  cell: (row) => formatPhoneNumber(row.phoneNumber),
                },
                {
                  key: 'tags',
                  header: 'Tags',
                  width: '150px',
                  cell: (row) => {
                    if (!Array.isArray(row.tags) || row.tags.length === 0)
                      return <span className="text-muted-foreground">-</span>;
                    return <ExpandableTagsCell tags={row.tags} />;
                  },
                },
                {
                  key: 'consent',
                  header: 'Consent',
                  width: '140px',
                  cell: (row) => <ChannelConsentIcons consent={row.consent} />,
                },
                {
                  key: 'flowCreatedAt',
                  header: 'Added to Flow',
                  width: '260px',
                  sortable: true,
                  cell: (row) => {
                    const val = row.flowCreatedAt as string | null;
                    if (!val) return '-';
                    return (
                      <span className="whitespace-nowrap">
                        {formatDate(val, organization?.timezone || 'UTC')}
                      </span>
                    );
                  },
                },
              ]}
              data={contacts}
              keyExtractor={(item) => item.id}
              loading={isLoading}
              emptyStateText="No contacts available"
              sortable
              onSort={(key, direction) => {
                setSortField(key);
                setSortDirection(direction);
              }}
            />
          </TableCard>
        </div>
      </Modal>

      {selectedContactId && (
        <EditContactModal
          isOpen={!!selectedContactId}
          onClose={() => setSelectedContactId(null)}
          onSubmit={async () => {}}
          onSuccess={() => {
            refetch();
          }}
          contact={{ id: selectedContactId }}
        />
      )}
    </>
  );
};
